"use client"

import {useEffect, useRef, useState} from "react"
import "./AudioOutputLevel.scss"

interface AudioOutputLevelProps {
    audioElement?: HTMLAudioElement | HTMLVideoElement | null
    audioSource?: MediaElementAudioSourceNode | AudioNode | null | undefined
    minThreshold?: number
    className?: string
}

export const AudioOutputLevel = ({
                                     audioElement = null,
                                     audioSource = null,
                                     minThreshold = 15,
                                     className = "",
                                 }: AudioOutputLevelProps) => {
    const [outputLevel, setOutputLevel] = useState<number>(0)
    const audioContextRef = useRef<AudioContext | null>(null)
    const analyserRef = useRef<AnalyserNode | null>(null)
    const sourceNodeRef = useRef<MediaElementAudioSourceNode | AudioNode | null>(null)
    const animationRef = useRef<number | null>(null)
    const setupGenRef = useRef<number>(0)

    useEffect(() => {
        // Increment generation so any in-flight async setup from a previous
        // effect invocation can detect it has been superseded and bail out.
        const gen = ++setupGenRef.current

        stopAudioProcessing()

        if (audioElement || audioSource) {
            setupAudioProcessing(gen)
        }

        return () => {
            stopAudioProcessing()
        }
    }, [audioElement, audioSource]) // eslint-disable-line react-hooks/exhaustive-deps

    const setupAudioProcessing = async (gen: number) => {
        try {
            if (!audioSource) return

            const audioContext = audioSource.context as AudioContext

            // Browsers suspend AudioContexts that were created before a user
            // gesture. The TTS AudioPlayer never calls resume() itself, so we
            // must do it here — exactly the same pattern used in AudioLevel.tsx.
            if (audioContext.state === 'suspended') {
                try { await audioContext.resume() } catch (_) {}
            }

            // Bail out if a newer effect invocation has superseded this one
            // (handles React StrictMode double-invoke and rapid audioSource changes).
            if (setupGenRef.current !== gen) return

            // If the context ended up in a bad state (e.g. closed), bail out.
            if (audioContext.state === 'closed') return

            const analyser = audioContext.createAnalyser()
            analyser.fftSize = 256
            analyser.smoothingTimeConstant = 0.5
            analyserRef.current = analyser

            // Tap into the audio chain without owning the context.
            // Store the source so we can disconnect ONLY from our analyser on cleanup.
            sourceNodeRef.current = audioSource
            sourceNodeRef.current.connect(analyser)

            // Keep a reference so stopAudioProcessing can guard against double-calls
            audioContextRef.current = audioContext

            updateAudioLevel()
        } catch (err) {
            console.error("Error processing audio output:", err)
        }
    }

    const stopAudioProcessing = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current)
            animationRef.current = null
        }

        // Disconnect ONLY the source → analyser edge.
        // Never call sourceNode.disconnect() with no args — that would sever
        // the entire TTS audio chain and silence playback.
        if (sourceNodeRef.current && analyserRef.current) {
            try {
                sourceNodeRef.current.disconnect(analyserRef.current)
            } catch (_) {
                // already disconnected — safe to ignore
            }
            sourceNodeRef.current = null
        }

        if (analyserRef.current) {
            analyserRef.current.disconnect()
            analyserRef.current = null
        }

        // We do NOT own the AudioContext (it belongs to the TTS chain), so we
        // must NOT close it here — just clear our reference.
        audioContextRef.current = null

        setOutputLevel(0)
    }

    const updateAudioLevel = () => {
        if (!analyserRef.current) return

        const bufferLength = analyserRef.current.frequencyBinCount
        const dataArray = new Uint8Array(bufferLength)

        const updateLevel = () => {
            if (!analyserRef.current) return

            analyserRef.current.getByteFrequencyData(dataArray)

            let sum = 0
            for (let i = 0; i < bufferLength; i++) {
                sum += dataArray[i]
            }
            const average = sum / bufferLength

            // TTS output is already normalized/compressed by Azure, so a hard threshold of 60
            // (tuned for microphone input) would immediately clip to 100%. A sqrt curve maps
            // the full 0–255 byte range smoothly to 0–100%, matching human loudness perception.
            const normalizedLevel = Math.round(Math.sqrt(Math.min(average / 255, 1)) * 100)
            setOutputLevel(normalizedLevel)

            animationRef.current = requestAnimationFrame(updateLevel)
        }

        animationRef.current = requestAnimationFrame(updateLevel)
    }

    const levelStatus = outputLevel > 0 ? "good" : "low"

    return (
        <div className={`audio-output-level ${className}`}>
            <div className="audio-output-level-meter">
                <div className={`audio-output-level-fill ${levelStatus}`} style={{width: `${outputLevel}%`}}/>
            </div>
        </div>
    )
}